<template>
  <div class="warehouse-packing-list-page p-4">
    <div class="flex items-center justify-between mb-4">
      <h3 class="text-xl font-semibold text-blue-900">
        {{ $t('packing_list') }}
      </h3>
      <div class="flex items-center gap-3">
        <b-button
          type="is-primary"
          size="is-small"
          icon-left="plus"
          icon-pack="fas"
          @click="openAddMaterialModal"
        >
          {{ $t('create_unique_material') }}
        </b-button>
        <b-button
          type="is-primary"
          size="is-small"
          icon-left="plus"
          icon-pack="fas"
          @click="onAddMaterial"
        >
          {{ $t('add_material') }}
        </b-button>
        <b-button
          type="is-primary"
          size="is-small"
          icon-left="upload"
          icon-pack="fas"
          @click="onUploadPackingList"
        >
          {{ $t('load_packing_list') }}
        </b-button>
        <b-button
          type="is-primary"
          size="is-small"
          icon-left="file-export"
          icon-pack="fas"
          :loading="isExporting"
          :disabled="isExporting"
          @click="onExportReceptionReport()"
        >
          {{ $t('export') }}
        </b-button>
      </div>
    </div>

    <b-collapse
      class="card filters-collapse mb-3"
      v-model="showListFilters"
    >
      <template #trigger>
        <div class="card-header filters-trigger">
          <p class="card-header-title">
            {{ $t('filters') }}
          </p>
          <a class="card-header-icon">
            <b-icon pack="fa" v-show="!showListFilters" icon="plus-circle" />
            <b-icon pack="fa" v-show="showListFilters" icon="minus-circle" />
          </a>
        </div>
      </template>

      <div class="panel" style="border-width: 0">
        <div class="grid grid-cols-1 md:grid-cols-3 gap-3">
          <b-field :label="$t('date_start')">
            <b-input
              v-model="listFilters.date_from"
              type="date"
              @input="onListFiltersChange"
            />
          </b-field>

          <b-field :label="$t('date_end')">
            <b-input
              v-model="listFilters.date_to"
              type="date"
              @input="onListFiltersChange"
            />
          </b-field>

          <b-field :label="$t('search')">
            <b-input
              v-model.trim="listFilters.search"
              :placeholder="$t('search')"
              @input="onListFiltersChangeDebounced"
            />
          </b-field>
        </div>
      </div>
    </b-collapse>

    <b-table
      :data="rows"
      :loading="isLoading"
      detailed
      detail-key="id"
      :opened-detailed="packingListTableOpenedDetails"
      @update:openedDetailed="packingListTableOpenedDetails = $event"
      :has-detailed-visible="row => Number(row.materials_count || 0) + Number(row.boxes_count || 0) > 0"
      paginated
      :per-page="25"
      striped
      bordered
      mobile-cards
      icon-pack="fas"
    >
      <b-table-column field="id" :label="$t('id')" width="80" v-slot="props">
        {{ props.row.id }}
      </b-table-column>

      <b-table-column field="number" :label="$t('packing_list_number')" width="160" v-slot="props">
        {{ props.row.number || '-' }}
      </b-table-column>

      <b-table-column field="date" :label="$t('date')" width="140" v-slot="props">
        {{ props.row.date }}
      </b-table-column>

      <b-table-column field="description" :label="$t('description')" v-slot="props">
        {{ props.row.description }}
      </b-table-column>

      <b-table-column field="materials_count" :label="`${$t('materials')} #`" width="170" v-slot="props">
        {{ props.row.materials_count }}
      </b-table-column>

      <b-table-column field="boxes_count" :label="`${$t('bulk')} #`" width="140" v-slot="props">
        {{ props.row.boxes_count }}
      </b-table-column>

      <b-table-column field="actions" :label="$t('actions')" centered width="130" v-slot="props">
        <div class="w-full h-full flex items-center justify-center gap-3">
          <b-tooltip :label="$t('details')" position="is-top">
            <div @click="onViewPackingList(props.row, props.toggleDetails)" class="cursor-pointer text-gray-600">
              <b-icon pack="fas" icon="layer-group" />
            </div>
          </b-tooltip>
          <b-tooltip :label="$t('export')" position="is-top">
            <div
              class="cursor-pointer text-gray-600"
              :class="{ 'opacity-50 pointer-events-none': isExporting }"
              @click="onExportReceptionReport(props.row)"
            >
              <b-icon pack="fas" icon="file-alt" />
            </div>
          </b-tooltip>
          <b-tooltip :label="$t('delete')" position="is-top" type="is-danger">
            <div
              role="button"
              tabindex="0"
              class="cursor-pointer text-red-600"
              :class="{ 'opacity-50 pointer-events-none': deletingPackingListId === props.row.id }"
              @click="onDeletePackingList(props.row)"
              @keydown.enter.prevent="onDeletePackingList(props.row)"
            >
              <b-icon pack="fas" icon="trash-alt" />
            </div>
          </b-tooltip>
        </div>
      </b-table-column>

      <template #detail="props">
        <div class="p-3 bg-gray-100 relative min-h-[48px]">
          <div class="warehouse-detail-scroll">
            <materials-list-table-bulks
              :project-id="normalizedProjectId"
              :packing-list-id="props.row.id"
              :packing-list-number="props.row.number != null && props.row.number !== '' ? String(props.row.number) : ''"
              :import-entity-type="importEntityType"
              vista="entradas"
              @import-success="handleImportSuccess"
            />
          </div>
        </div>
      </template>
    </b-table>
    <warehouse-import-packing-list-modal
      v-model="isImportModalActive"
      :project-id="normalizedProjectId"
      :import-entity-type="importEntityType"
      :extra-payload="importExtraPayload"
      @success="handleImportSuccess"
    />

    <warehouse-add-inventory-material-modal
      v-model="isCreateMaterialModalActive"
      :project-id="normalizedProjectId"
      :parent-inventory-id="createModalParentInventoryId"
      :packing-list-id="createModalPackingListId"
      :preset-packing-list-number="createModalPresetPackingListNumber"
      @saved="onMaterialSaved"
      @error="onAddMaterialError"
    />

    <WarehouseAddCatalogMaterialModal
      ref="addMaterialModal"
      :project-id="normalizedProjectId"
      :allow-edit="false"
    />
  </div>
</template>

<script>
import MaterialsListTableBulks from './materials-list-table-bulks.vue'
import WarehouseAddInventoryMaterialModal from './warehouse-add-inventory-material-modal.vue'
import WarehouseAddCatalogMaterialModal from '../../admin/warehouse/warehouse-add-material-modal.vue'
import WarehouseImportPackingListModal from './warehouse-import-packing-list-modal.vue'
import axios from 'axios'

export default {
  name: 'warehouse-packing-list',
  components: {
    MaterialsListTableBulks,
    WarehouseAddInventoryMaterialModal,
    WarehouseAddCatalogMaterialModal,
    WarehouseImportPackingListModal,
  },
  props: {
    projectId: {
      type: [String, Number],
      required: true,
    },
    importEntityType: {
      type: String,
      default: 'warehouse_packing_list',
    },
  },
  computed: {
    normalizedProjectId() {
      return Number(this.projectId) || null
    },
  },
  data: () => ({
    isImportModalActive: false,
    isCreateMaterialModalActive: false,
    createModalParentInventoryId: null,
    createModalPackingListId: null,
    createModalPresetPackingListNumber: '',
    isLoading: false,
    loadError: null,
    importExtraPayload: {},
    packingListTableOpenedDetails: [],
    rows: [],
    isExporting: false,
    deletingPackingListId: null,
    showListFilters: false,
    listFilters: {
      date_from: '',
      date_to: '',
      search: '',
    },
    listFiltersDebounce: null,
  }),
  created() {
    this.loadPackingLists()
  },
  watch: {
    normalizedProjectId() {
      this.loadPackingLists()
    },
    // Al cerrar el modal de importación, refrescamos la lista
    isImportModalActive(newVal) {
      if (!newVal) {
        this.loadPackingLists()
      }
    },
  },
  methods: {
    extractArrayFromResponse(data, key) {
      if (Array.isArray(data && data[key])) return data[key]
      if (Array.isArray(data && data.data && data.data[key])) return data.data[key]
      return []
    },
    async loadPackingLists() {
      if (!this.normalizedProjectId) {
        this.rows = []
        return
      }

      this.isLoading = true
      this.loadError = null
      try {
        const { data } = await axios.get(`/api/v2/project/${this.normalizedProjectId}/warehouse/packing-lists`, {
          params: {
            per_page: 50,
            page: 1,
            order_field: 'id',
            order_dir: 'desc',
            ...this.listFilterParams(),
          },
        })

        const items = this.extractArrayFromResponse(data, 'packing_lists')
        this.rows = items.map((p) => ({
          ...p,
          date: p.created_at ? String(p.created_at).slice(0, 10) : '',
        }))
      } catch (e) {
        this.loadError = e
        this.rows = []
      } finally {
        this.isLoading = false
      }
    },
    listFilterParams() {
      const f = this.listFilters || {}
      const search = f.search ? String(f.search).trim() : ''
      return {
        date_from: f.date_from || undefined,
        date_to: f.date_to || undefined,
        search: search !== '' ? search : undefined,
      }
    },
    onListFiltersChange() {
      this.loadPackingLists()
    },
    onListFiltersChangeDebounced() {
      if (this.listFiltersDebounce) clearTimeout(this.listFiltersDebounce)
      this.listFiltersDebounce = setTimeout(() => {
        this.loadPackingLists()
      }, 350)
    },
    onAddMaterial() {
      this.createModalParentInventoryId = null
      this.createModalPackingListId = null
      this.createModalPresetPackingListNumber = ''
      this.isCreateMaterialModalActive = true
    },
    async openAddMaterialModal() {
      if (this.$refs.addMaterialModal && this.$refs.addMaterialModal.open) {
        await this.$refs.addMaterialModal.open()
      }
    },
    onMaterialSaved() {
      this.packingListTableOpenedDetails = []
      this.loadPackingLists()
    },
    onAddMaterialError(e) {
      this.loadError = e
    },
    onUploadPackingList() {
      this.importExtraPayload = {}
      this.isImportModalActive = true
    },
    onViewPackingList(row, toggleDetails) {
      if (typeof toggleDetails === 'function') {
        toggleDetails(row)
      }
    },
    firstValidationMessage(errorData) {
      const err = errorData && errorData.errors
      if (err && typeof err === 'object') {
        const firstKey = Object.keys(err)[0]
        if (firstKey && Array.isArray(err[firstKey]) && err[firstKey].length) {
          return err[firstKey][0]
        }
      }
      if (errorData && errorData.message) {
        return errorData.message
      }
      return null
    },
    onDeletePackingList(row) {
      if (!row || row.id == null) return
      const packingListId = Number(row.id)
      const label = row.number ? String(row.number) : String(packingListId)
      this.$buefy.dialog.confirm({
        title: this.$t('delete'),
        message: this.$t('delete_packing_list_confirm_text', [label]),
        confirmText: this.$t('delete'),
        cancelText: this.$t('cancel'),
        type: 'is-danger',
        hasIcon: true,
        onConfirm: async () => {
          this.deletingPackingListId = packingListId
          try {
            const { data } = await axios.delete(
              `/api/v2/project/${this.normalizedProjectId}/warehouse/packing-lists/${packingListId}`
            )
            if (data && data.success) {
              this.$buefy.toast.open({
                message: this.$t('deleted_successfully'),
                type: 'is-success',
                position: 'is-top-right',
              })
              this.loadPackingLists()
              this.$emit('deleted')
            } else {
              const msg = this.firstValidationMessage(data) || this.$t('error')
              this.$buefy.toast.open({ message: msg, type: 'is-danger', position: 'is-top-right' })
            }
          } catch (e) {
            const resp = e.response && e.response.data
            const msg = this.firstValidationMessage(resp) || (e.message ? String(e.message) : '') || this.$t('error')
            console.error('[warehouse-packing-list] delete failed', { status: e.response && e.response.status, response: resp, error: e })
            this.$buefy.toast.open({ message: msg, type: 'is-danger', position: 'is-top-right' })
          } finally {
            this.deletingPackingListId = null
          }
        },
      })
    },
    onViewDetail(row) {
      this.$emit('view-packing-list-detail', row)
    },
    async onExportReceptionReport(row = null) {
      if (!this.normalizedProjectId) return
      const packingListId = row && row.id != null ? Number(row.id) : null
      const isSingle = Number.isFinite(packingListId) && packingListId >= 1
      const filters = { project_id: this.normalizedProjectId }
      if (isSingle) filters.warehouse_packing_list_id = packingListId

      this.isExporting = true
      try {
        const res = await axios.post(
          '/api/v2/export/warehouse-reception',
          { filters },
          { responseType: 'blob' }
        )
        const contentDisposition = (res && res.headers && (res.headers['content-disposition'] || res.headers['Content-Disposition'])) || ''
        const match = /filename\*?=(?:UTF-8''|")?([^;"\n]+)/i.exec(contentDisposition)
        const fallbackName = 'Informe_IRM_Recepción.xlsx'
        const filename =
          (match && match[1] ? decodeURIComponent(match[1].replace(/"/g, '').trim()) : '') || fallbackName

        const blob = new Blob([res.data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        })
        const url = window.URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = filename
        document.body.appendChild(a)
        a.click()
        a.remove()
        window.URL.revokeObjectURL(url)
      } catch (e) {
        try {
          const msg = (e && e.response && e.response.data && e.response.data.message) || (e && e.message) || this.$t('error')
          if (this.$buefy && this.$buefy.toast && this.$buefy.toast.open) {
            this.$buefy.toast.open({ message: msg, type: 'is-danger' })
          }
        } catch (_) {
          // ignore toast rendering errors
        }
        console.error(e)
      } finally {
        this.isExporting = false
      }
    },
    handleImportSuccess() {
      this.loadPackingLists()
      this.$emit('import-success')
    },
  },
}
</script>

<style scoped>
.min-w-160 {
  min-width: 160px;
}
.warehouse-detail-scroll {
  width: 100%;
  max-width: 100%;
  overflow-x: auto;
}
::v-deep tr.detail > td {
  max-width: 0;
}
</style>
